import React, { memo, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import type { PDFPageProxy } from 'pdfjs-dist';
import HTMLFlipBook from 'react-pageflip';
import { PDFPage } from '../PDFPage';
import { PDFLoadingScreen } from '../PDFLoadingScreen';
import { usePDFViewer, usePDFViewerStores, useViewerStore } from '../../hooks';
import { cn } from '../../utils';

/**
 * How many pages on each side of the current page to keep loaded in book
 * mode. Anything outside this window renders as an empty placeholder until
 * the user flips towards it. This is what prevents a 400 MB PDF from
 * fetching every page just to display the first spread.
 */
const BOOK_MODE_OVERSCAN = 4;

export interface BookModeContainerProps {
  className?: string;
  /** Flip animation duration in ms (default: 800) */
  flippingTime?: number;
  /** Draw page shadows during flip (default: true) */
  drawShadow?: boolean;
  /** Max shadow opacity 0-1 (default: 0.7) */
  maxShadowOpacity?: number;
}

/** A single page inside the flipbook — must use forwardRef for react-pageflip */
const BookPage = React.forwardRef<HTMLDivElement, {
  pageNumber: number;
  page: PDFPageProxy | null;
  scale: number;
  rotation: number;
  width: number;
  height: number;
}>(function BookPage({ pageNumber, page, scale, rotation, width, height }, ref) {
  return (
    <div ref={ref} className="book-page" data-page-number={pageNumber}>
      <div style={{ width, height, overflow: 'hidden' }}>
        <PDFPage
          pageNumber={pageNumber}
          page={page}
          scale={scale}
          rotation={rotation}
          showTextLayer={false}
          showAnnotationLayer={false}
          showHighlightLayer={false}
        />
      </div>
    </div>
  );
});

export const BookModeContainer = memo(function BookModeContainer({
  className,
  flippingTime = 800,
  drawShadow = true,
  maxShadowOpacity = 0.7,
}: BookModeContainerProps) {
  const {
    document,
    currentPage,
    numPages,
    scale,
    rotation,
    theme,
    isLoading,
    goToPage,
  } = usePDFViewer();

  const scrollToPageRequest = useViewerStore((s) => s.scrollToPageRequest);
  const { viewerStore } = usePDFViewerStores();

  // Lazily-populated map of page proxies. Pages outside the overscan window
  // around `currentPage` are simply absent — they render as empty placeholders
  // and only get fetched when the user flips towards them.
  const [pages, setPages] = useState<Map<number, PDFPageProxy>>(new Map());
  // Raw PDF page dimensions (at scale=1, rotation=0)
  const [rawPageDims, setRawPageDims] = useState({ width: 612, height: 792 });
  const [hasFirstPage, setHasFirstPage] = useState(false);
  const pageRequestsRef = useRef<Set<number>>(new Set());

  // Container measurement for responsive sizing
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Flipbook ref for programmatic control
  const flipBookRef = useRef<any>(null);

  // Track whether we're syncing from flipbook → store (to avoid loops)
  const isSyncingRef = useRef(false);

  // ─── Measure container ──────────────────────────────────────────────

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      setContainerSize({ width: el.clientWidth, height: el.clientHeight });
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ─── Reset cached pages when the document changes ─────────────────

  useEffect(() => {
    setPages(new Map());
    pageRequestsRef.current.clear();
    setHasFirstPage(false);
  }, [document]);

  // ─── Lazily load pages near the current spread ────────────────────
  //
  // This is the load-everything-at-once fix for book mode. We never iterate
  // `numPages` here — for a 400 MB PDF that fetches the entire document just
  // to render the first spread.

  useEffect(() => {
    if (!document || numPages === 0) return;

    const start = Math.max(1, currentPage - BOOK_MODE_OVERSCAN);
    const end = Math.min(numPages, currentPage + BOOK_MODE_OVERSCAN);
    const isFirstLoad = !hasFirstPage;

    let cancelled = false;
    const wanted: number[] = [];
    if (isFirstLoad && !pages.has(1) && !pageRequestsRef.current.has(1)) {
      // Always request page 1 first — we need its viewport for
      // `rawPageDims`, which determines the flipbook's display size.
      wanted.push(1);
      pageRequestsRef.current.add(1);
    }
    for (let i = start; i <= end; i++) {
      if (pages.has(i) || pageRequestsRef.current.has(i)) continue;
      wanted.push(i);
      pageRequestsRef.current.add(i);
    }

    if (wanted.length === 0) return;

    (async () => {
      const results = await Promise.allSettled(
        wanted.map((i) => document.getPage(i).then((p) => [i, p] as const))
      );
      if (cancelled) return;

      const next = new Map(pages);
      let firstPageJustLoaded: PDFPageProxy | null = null;
      for (const r of results) {
        if (r.status === 'fulfilled') {
          const [i, page] = r.value;
          next.set(i, page);
          if (i === 1) firstPageJustLoaded = page;
        }
      }
      // Drop pages outside the window so memory doesn't grow unbounded as the
      // user flips through a long book. Page 1 is kept so we can always
      // recover its viewport for `rawPageDims`.
      for (const i of next.keys()) {
        if (i !== 1 && (i < start || i > end)) {
          next.delete(i);
        }
      }
      setPages(next);

      if (firstPageJustLoaded) {
        const vp = firstPageJustLoaded.getViewport({ scale: 1, rotation });
        setRawPageDims({ width: vp.width, height: vp.height });
        setHasFirstPage(true);
      }

      for (const i of wanted) {
        pageRequestsRef.current.delete(i);
      }
    })();

    return () => {
      cancelled = true;
      for (const i of wanted) {
        pageRequestsRef.current.delete(i);
      }
    };
  }, [document, numPages, currentPage, rotation, pages, hasFirstPage]);

  // ─── Update raw page dimensions when rotation changes ──────────────

  useEffect(() => {
    const firstPage = pages.get(1);
    if (firstPage) {
      const vp = firstPage.getViewport({ scale: 1, rotation });
      setRawPageDims({ width: vp.width, height: vp.height });
    }
  }, [pages, rotation]);

  // ─── Compute fitted dimensions ─────────────────────────────────────

  const padding = 8; // px padding around the flipbook
  const fitWidth = Math.max(containerSize.width - padding * 2, 200);
  const fitHeight = Math.max(containerSize.height - padding * 2, 300);

  const pageAspect = rawPageDims.width / rawPageDims.height;
  let displayWidth: number;
  let displayHeight: number;

  // Fit within container maintaining aspect ratio
  if (fitWidth / fitHeight > pageAspect) {
    // Container is wider — height is the constraint
    displayHeight = fitHeight;
    displayWidth = Math.floor(fitHeight * pageAspect);
  } else {
    // Container is taller — width is the constraint
    displayWidth = fitWidth;
    displayHeight = Math.floor(fitWidth / pageAspect);
  }

  // Compute the scale needed to render the PDF page at the display size
  const renderScale = displayWidth / rawPageDims.width;

  // ─── Sync viewer store → flipbook (e.g. toolbar page input) ──────

  useEffect(() => {
    const pageFlip = flipBookRef.current?.pageFlip();
    if (!pageFlip) return;

    const flipBookPage = pageFlip.getCurrentPageIndex();
    const targetIndex = currentPage - 1; // flipbook is 0-indexed

    if (flipBookPage !== targetIndex) {
      isSyncingRef.current = true;
      pageFlip.turnToPage(targetIndex);
      // Reset sync flag after a short delay
      setTimeout(() => { isSyncingRef.current = false; }, 100);
    }
  }, [currentPage]);

  // Handle scroll-to-page requests
  useEffect(() => {
    if (scrollToPageRequest) {
      requestAnimationFrame(() => {
        viewerStore.getState().completeScrollRequest(scrollToPageRequest.requestId);
      });
    }
  }, [scrollToPageRequest, viewerStore]);

  // ─── Flipbook → viewer store sync (on page flip) ─────────────────

  const handleFlip = useCallback((e: { data: number }) => {
    if (isSyncingRef.current) return;
    const newPage = e.data + 1; // flipbook is 0-indexed, viewer is 1-indexed
    if (newPage !== currentPage && newPage >= 1 && newPage <= numPages) {
      goToPage(newPage);
    }
  }, [currentPage, numPages, goToPage]);

  // ─── Theme ────────────────────────────────────────────────────────

  const themeStyles = {
    light: 'bg-gray-100',
    dark: 'bg-gray-900',
    sepia: 'bg-amber-50',
  };

  const themeClass = theme === 'dark' ? 'dark' : theme === 'sepia' ? 'sepia' : '';

  // We're "ready" as soon as we have page 1's viewport (and therefore the
  // flipbook display size). Other pages render placeholders until they
  // stream in.
  const ready = !!document && hasFirstPage;
  const hasContainer = containerSize.width > 0 && containerSize.height > 0;

  // Build the children for HTMLFlipBook lazily — react-pageflip needs one
  // child per page, but each is just a thin <BookPage> that renders an empty
  // placeholder until its `page` proxy is in the map.
  const pageChildren = useMemo(() => {
    if (numPages === 0) return null;
    const out = new Array<React.ReactNode>(numPages);
    for (let i = 0; i < numPages; i++) {
      const pageNumber = i + 1;
      out[i] = (
        <BookPage
          key={pageNumber}
          pageNumber={pageNumber}
          page={pages.get(pageNumber) ?? null}
          scale={renderScale}
          rotation={rotation}
          width={displayWidth}
          height={displayHeight}
        />
      );
    }
    return out;
  }, [numPages, pages, renderScale, rotation, displayWidth, displayHeight]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'book-mode-container',
        'flex-1 h-full w-full overflow-hidden',
        'flex items-center justify-center',
        themeStyles[theme],
        themeClass,
        className
      )}
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      {!ready && (
        <PDFLoadingScreen
          phase={!document ? (isLoading ? 'fetching' : 'initializing') : 'rendering'}
        />
      )}

      {ready && hasContainer && (
        <HTMLFlipBook
          ref={flipBookRef}
          width={displayWidth}
          height={displayHeight}
          size="fixed"
          minWidth={displayWidth}
          maxWidth={displayWidth}
          minHeight={displayHeight}
          maxHeight={displayHeight}
          drawShadow={drawShadow}
          maxShadowOpacity={maxShadowOpacity}
          flippingTime={flippingTime}
          usePortrait={true}
          startPage={currentPage - 1}
          showCover={false}
          mobileScrollSupport={true}
          swipeDistance={30}
          showPageCorners={true}
          useMouseEvents={true}
          clickEventForward={false}
          onFlip={handleFlip}
          className="book-flipbook"
          style={{}}
          startZIndex={0}
          autoSize={false}
          renderOnlyPageLengthChange={false}
          disableFlipByClick={false}
        >
          {pageChildren}
        </HTMLFlipBook>
      )}

    </div>
  );
});
